import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type UserFilterState = {
  searchTerm: string;
  status: string[];
  roles: string[];
  hasChatwoot: boolean | null; // null = todos, true = vinculado, false = não vinculado
  overdueOnly: boolean | null; // null = todos, true = plano vencido
};

type UserFiltersProps = {
  filters: UserFilterState;
  onFiltersChange: (filters: UserFilterState) => void;
  statuses: string[];
  roles: Array<{ id: string; name: string }>;
  counts?: {
    status: Record<string, number>;
    roles: Record<string, number>;
    hasChatwoot: {
      vinculado: number;
      naoVinculado: number;
    };
    overdue?: {
      vencido: number;
    };
  };
  hasChatwootToken?: boolean;
};

type TabType = "resumo" | "status" | "perfis" | "chatwoot" | "assinatura";

export function UserFilters({
  filters,
  onFiltersChange,
  statuses,
  roles,
  counts,
  hasChatwootToken = false,
}: UserFiltersProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("resumo");

  const filterCount = 
    filters.status.length +
    filters.roles.length +
    (hasChatwootToken && filters.hasChatwoot !== null ? 1 : 0) +
    (filters.overdueOnly ? 1 : 0);

  const clearAllFilters = () => {
    onFiltersChange({
      searchTerm: filters.searchTerm,
      status: [],
      roles: [],
      hasChatwoot: null,
      overdueOnly: null,
    });
  };

  const removeFilter = (type: keyof UserFilterState, value?: string) => {
    if (type === "status" || type === "roles") {
      onFiltersChange({
        ...filters,
        [type]: value ? filters[type].filter((v) => v !== value) : [],
      });
    } else if (type === "hasChatwoot") {
      onFiltersChange({ ...filters, hasChatwoot: null });
    }
  };

  const tabs: Array<{ id: TabType; label: string; icon: string }> = [
    { id: "resumo", label: "Resumo", icon: "📋" },
    { id: "status", label: "Status", icon: "📊" },
    { id: "perfis", label: "Perfis", icon: "👤" },
    ...(hasChatwootToken ? [{ id: "chatwoot" as TabType, label: "Chatwoot", icon: "💬" }] : []),
    { id: "assinatura", label: "Assinatura", icon: "💳" },
  ];

  const getTabCount = (tabId: TabType) => {
    switch (tabId) {
      case "status": return filters.status.length;
      case "perfis": return filters.roles.length;
      case "chatwoot": return filters.hasChatwoot !== null ? 1 : 0;
      case "assinatura": return filters.overdueOnly ? 1 : 0;
      default: return 0;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip open={!open ? undefined : false}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="outline" className="relative">
                <Filter className="h-4 w-4 mr-2" />
                Filtros
                {filterCount > 0 && (
                  <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center" variant="default">
                    {filterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Filtre a listagem de usuários</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="w-[500px] p-0" align="start">
        <div className="flex h-[400px]">
          {/* Sidebar */}
          <div className="w-48 border-r bg-muted/30 p-2 overflow-y-auto">
            {tabs.map((tab) => {
              const count = getTabCount(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded text-sm mb-1",
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </span>
                  {count > 0 && (
                    <Badge variant={activeTab === tab.id ? "secondary" : "default"} className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {count}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 p-4 overflow-y-auto">
            {activeTab === "resumo" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Resumo dos Filtros</h3>
                  {filterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                      Limpar tudo
                    </Button>
                  )}
                </div>

                {filterCount === 0 ? (
                  <p className="text-muted-foreground text-sm">Nenhum filtro aplicado</p>
                ) : (
                  <div className="space-y-3">
                    {filters.status.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Status</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.status.map((s) => (
                            <Badge key={s} variant="secondary" className="gap-1 capitalize">
                              {s}
                              <X
                                className="h-3 w-3 cursor-pointer"
                                onClick={() => removeFilter("status", s)}
                              />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {filters.roles.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Perfis</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.roles.map((roleId) => {
                            const role = roles.find((r) => r.id === roleId);
                            return (
                              <Badge key={roleId} variant="secondary" className="gap-1">
                                {role?.name || roleId}
                                <X
                                  className="h-3 w-3 cursor-pointer"
                                  onClick={() => removeFilter("roles", roleId)}
                                />
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {hasChatwootToken && filters.hasChatwoot !== null && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Chatwoot</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            {filters.hasChatwoot ? "Vinculado" : "Não vinculado"}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeFilter("hasChatwoot")}
                            />
                          </Badge>
                        </div>
                      </div>
                    )}
                    {filters.overdueOnly && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Assinatura</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            Plano vencido (sem pagamento)
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => onFiltersChange({ ...filters, overdueOnly: null })}
                            />
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "status" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Status</h3>
                <div className="space-y-2">
                  {statuses.map((status) => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${status}`}
                        checked={filters.status.includes(status)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            status: checked
                              ? [...filters.status, status]
                              : filters.status.filter((s) => s !== status),
                          });
                        }}
                      />
                      <Label htmlFor={`status-${status}`} className="capitalize flex-1 flex items-center gap-2">
                        {status}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                          {counts?.status?.[status] ?? 0}
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "perfis" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Perfis</h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {roles.map((role) => (
                    <div key={role.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`role-${role.id}`}
                        checked={filters.roles.includes(role.id)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            roles: checked
                              ? [...filters.roles, role.id]
                              : filters.roles.filter((r) => r !== role.id),
                          });
                        }}
                      />
                      <Label htmlFor={`role-${role.id}`} className="flex-1 flex items-center gap-2">
                        {role.name}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                          {counts?.roles?.[role.id] ?? 0}
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasChatwootToken && activeTab === "chatwoot" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Status Chatwoot</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="chatwoot-vinculado"
                      checked={filters.hasChatwoot === true}
                      onCheckedChange={(checked) => {
                        onFiltersChange({
                          ...filters,
                          hasChatwoot: checked ? true : null,
                        });
                      }}
                    />
                    <Label htmlFor="chatwoot-vinculado" className="flex-1 flex items-center gap-2">
                      Vinculado
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                        {counts?.hasChatwoot?.vinculado ?? 0}
                      </Badge>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="chatwoot-nao-vinculado"
                      checked={filters.hasChatwoot === false}
                      onCheckedChange={(checked) => {
                        onFiltersChange({
                          ...filters,
                          hasChatwoot: checked ? false : null,
                        });
                      }}
                    />
                    <Label htmlFor="chatwoot-nao-vinculado" className="flex-1 flex items-center gap-2">
                      Não vinculado
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                        {counts?.hasChatwoot?.naoVinculado ?? 0}
                      </Badge>
                    </Label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "assinatura" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Status da Assinatura do Cliente</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="assinatura-vencida"
                      checked={Boolean(filters.overdueOnly)}
                      onCheckedChange={(checked) => {
                        onFiltersChange({
                          ...filters,
                          overdueOnly: checked ? true : null,
                        });
                      }}
                    />
                    <Label htmlFor="assinatura-vencida" className="flex-1 flex items-center gap-2">
                      Plano vencido (sem pagamento)
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                        {counts?.overdue?.vencido ?? 0}
                      </Badge>
                    </Label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

